import React, { Fragment } from 'react';

import { Select } from '@aline/common';
import { IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { AlineButton, AlineTooltip, panelHeaderHeightPx, Spacer } from '../generic';
import { CloseIcon } from '../icons/CloseIcon';

const pageHeaderHeightPx = panelHeaderHeightPx;
const useStyles = makeStyles()(theme => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    height: pageHeaderHeightPx,
    width: '100%',
  },
  appBarBody: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    height: '100%',
    padding: theme.spacing(0.5, 1),
    position: 'relative',
    width: '100%',
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
  closeIcon: {
    height: 16,
    width: 16,
  },
}));

type PageHeaderProps = {
  activeStep: number;
  next: (e: React.MouseEvent) => void;
  onClose: () => void;
  pageTitle: string;
  steps: number[];
  back?: (e: React.MouseEvent) => void;
  backDisabledReason?: string;
  cancelDataCy?: string;
  primaryActionLoading?: boolean;
  primaryActionTitle?: string;
  primaryDataCy?: string;
  primaryDisabledReason?: string;
};

export function PageHeader(props: PageHeaderProps) {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.appBar}>
        <PageHeaderBody {...props} />
      </div>
    </>
  );
}

export function PageHeaderBody({
  activeStep,
  back,
  backDisabledReason = '',
  cancelDataCy,
  next,
  onClose,
  pageTitle,
  primaryActionLoading,
  primaryActionTitle = 'Continue',
  primaryDataCy = Select.components.quickStart.button.continue,
  primaryDisabledReason = '',
  steps,
}: PageHeaderProps) {
  const { classes } = useStyles();
  const isPrimaryDisabled = Boolean(primaryDisabledReason);

  return (
    <div className={classes.appBarBody}>
      <IconButton className={classes.closeButton} data-cy={cancelDataCy} onClick={onClose}>
        <CloseIcon className={classes.closeIcon} />
      </IconButton>
      <Typography noWrap variant="subtitle1">
        {pageTitle}
      </Typography>
      <Stepper activeStep={activeStep} steps={steps} />
      <Spacer />
      {back && (
        <AlineTooltip arrow placement="bottom" title={backDisabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(backDisabledReason || primaryActionLoading)}
              variant="outlined"
              onClick={back}
            >
              Go Back
            </AlineButton>
          </div>
        </AlineTooltip>
      )}
      <AlineTooltip arrow placement="bottom" title={primaryDisabledReason}>
        <div>
          <AlineButton
            color="primary"
            data-cy={primaryDataCy}
            disabled={isPrimaryDisabled}
            loading={primaryActionLoading}
            variant="contained"
            onClick={next}
          >
            {primaryActionTitle}
          </AlineButton>
        </div>
      </AlineTooltip>
    </div>
  );
}

const useStepperStyles = makeStyles()(theme => ({
  connector: {
    backgroundColor: theme.appColors.rule,
    height: 1,
    width: 48,
  },
  steps: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
    left: '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
  },
}));

interface StepperProps {
  activeStep: number;
  steps: number[];
}

export function Stepper({ activeStep, steps }: StepperProps) {
  const { classes } = useStepperStyles();

  return (
    <div className={classes.steps}>
      {steps.map((number, index) => {
        const isLast = index === steps.length - 1;

        return (
          <Fragment key={index}>
            <Step key={index} active={activeStep === number} step={number} />
            {!isLast && <div className={classes.connector} />}
          </Fragment>
        );
      })}
    </div>
  );
}

const usePageStepsStyles = makeStyles<{ active: boolean }>()((theme, { active }) => ({
  step: {
    alignItems: 'center',
    backgroundColor: active ? `#324A52` : undefined,
    border: !active ? theme.appBorders.outline : `1px solid transparent`,
    borderRadius: '50%',
    boxSizing: 'border-box',
    color: active ? theme.palette.common.white : undefined,
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
}));

export function Step({ active, step }: { active: boolean; step: number }) {
  const { classes } = usePageStepsStyles({ active });

  return <Typography className={classes.step}>{step}</Typography>;
}
